import { PauseCircleOutlined, PlayCircleOutlined } from '@easyv/react-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TwinAnimationMixer } from '@easytwin/runtime';
import { useDumbState } from '@/hooks';
import { isNil, clamp, formatDurationAuto } from '@/utils';
import { Gizmo } from '../Gizmo';

interface BaseTimelineProps {
  target: { mixer: TwinAnimationMixer };
  pause: () => void;
  start: (time: number) => void;
  preview: (time: number) => void;
}

const TIMELINE_WIDTH = 200;

export function BaseTimeline(props: BaseTimelineProps) {
  const { target, pause, start, preview } = props;

  const [nowTime, setNowTime] = useState(0);
  const [isStopAtLastFrame, setIsStopAtLastFrame] = useState(false);

  const nowTimeStr = formatDurationAuto(nowTime);
  const endTime = target.mixer.getDuration();
  const endTimeStr = formatDurationAuto(endTime);
  const refresh = useDumbState();
  const timelineTrackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mixer = target.mixer;
    const onUpdate = (time: number) => {
      setNowTime(time);
    };

    const onStop = () => {
      setNowTime(0);
      // 无法从isPlaying和isPause判断是否播放结束，只能接受stop事件判断
      setIsStopAtLastFrame(true);
    };

    const onStart = () => {
      setIsStopAtLastFrame(false);
    };

    mixer.addListener('update', onUpdate);
    mixer.addListener('pause', refresh);
    mixer.addListener('start', onStart);
    mixer.addListener('preview', onUpdate);
    mixer.addListener('stop', onStop);

    return () => {
      mixer.removeListener('update', onUpdate);
      mixer.removeListener('pause', refresh);
      mixer.removeListener('start', refresh);
      mixer.removeListener('preview', onUpdate);
      mixer.removeListener('stop', onStop);
    };
  }, [refresh, target.mixer]);

  const handleClickTimeCanvas = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();

      const onMouseMove = (e: MouseEvent) => {
        const scale = TIMELINE_WIDTH / endTime; // 每秒多少像素
        if (isNil(timelineTrackRef.current)) return;

        const offsetX = e.clientX - timelineTrackRef.current.getBoundingClientRect().x;
        const time = clamp(offsetX / scale, 0, endTime);
        preview(time);
      };

      const onMouseUp = (e: MouseEvent) => {
        onMouseMove(e);

        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove, false);
      document.addEventListener('mouseup', onMouseUp, false);
    },
    [endTime, preview],
  );

  return (
    <div className='px-2 h-9 border border-FFFFFF0F rounded-lg bg-bgColor17171A flex items-center'>
      {target.mixer.isPlaying && !isStopAtLastFrame ? (
        <Gizmo
          wrapperClassType='hoverIcon'
          icon={<PauseCircleOutlined size={16} />}
          onClick={() => {
            pause();
          }}
        />
      ) : (
        <Gizmo
          wrapperClassType='hoverIcon'
          icon={<PlayCircleOutlined size={16} />}
          onClick={() => {
            start(nowTime);
            console.log('start at time', nowTime);
          }}
        />
      )}
      <div className='ml-4'>{nowTimeStr}</div>
      <div
        className='mx-2 flex items-center relative cursor-pointer'
        ref={timelineTrackRef}
        onMouseDown={handleClickTimeCanvas}
      >
        <div className='h-1 bg-FFFFFF14' style={{ width: TIMELINE_WIDTH }}></div>
        <div
          className='absolute h-1 bg-FFFFFF3D'
          style={{ width: `${(nowTime / endTime) * 100}%` }}
        />
        <div
          className='absolute h-2 w-[3px] bg-content2 rounded-md cursor-pointer'
          style={{ left: `${TIMELINE_WIDTH * (nowTime / endTime) - 3 / 2}px` }}
        />
      </div>
      <div>{endTimeStr}</div>
    </div>
  );
}
